/**
 * The hop's arc and its ground shadow (ADR-116's third amendment of 2026-10-08): how far the drawn character rises
 * on each frame of the `jump` strip, and how wide the shadow drawn on its feet line is at each lift. A picture only:
 * the true feet, the sort and everything on the server stay where the character stands.
 */

/**
 * The strip the arc lifts.
 */
export const JUMP_STRIP = "jump";

/**
 * The highest lift, in world pixels: the one knob that tunes the hop's height.
 */
export const JUMP_LIFT_PEAK = 10;

/**
 * The lift on each frame of the `jump` strip as a share of the peak: the two crouches on the ground, the three rise
 * frames climbing, the top on the first air frame and the other two falling, then the landing crouch. The rise frame's
 * toes hang 2 pixels below the standing feet line in the pack's art; its first share of a quarter of the peak of 10
 * lifts them 1 pixel clear of the ground.
 */
export const JUMP_ARC: readonly number[] = [0, 0, 0.25, 0.625, 0.875, 1, 0.875, 0.5, 0, 0];

/**
 * The shadow's width at the lowest lift, in world pixels: the pack's larger jump shadow.
 */
export const JUMP_SHADOW_WIDEST = 13;

/**
 * The shadow's width at the peak, in world pixels.
 */
export const JUMP_SHADOW_NARROWEST = 9;

/**
 * The narrowest shadow width drawn 4 pixels high; narrower widths are 3.
 */
const JUMP_SHADOW_FLAT_BELOW = 11;

/**
 * How far the drawn character stands above its feet on one frame of the `jump` strip, in whole world pixels.
 *
 * @param frame The frame shown, its place in the strip.
 * @param peak The highest lift; `JUMP_LIFT_PEAK` when omitted.
 * @returns The lift, 0 on a frame the arc has no share for.
 */
export function jumpLift(frame: number, peak: number = JUMP_LIFT_PEAK): number {
  const share = JUMP_ARC[frame];
  return share === undefined ? 0 : Math.round(share * peak);
}

/**
 * How wide the ground shadow under a lifted character is, in whole world pixels: the widest at the lowest lift and the
 * narrowest from the peak up, always odd, so it centres on the feet.
 *
 * @param lift The lift in world pixels.
 * @param peak The highest lift; `JUMP_LIFT_PEAK` when omitted.
 * @returns The width, 0 on the ground, where no shadow is drawn.
 */
export function jumpShadowWidth(lift: number, peak: number = JUMP_LIFT_PEAK): number {
  if (lift <= 0) {
    return 0;
  }
  const steps = (JUMP_SHADOW_WIDEST - JUMP_SHADOW_NARROWEST) / 2;
  return JUMP_SHADOW_WIDEST - 2 * Math.round((steps * Math.min(lift, peak)) / peak);
}

/**
 * How high the ground shadow of a width is, in whole world pixels.
 *
 * @param width The shadow's width, from `jumpShadowWidth`.
 * @returns 4 at 11 or wider, 3 when narrower, and 0 for no shadow.
 */
export function jumpShadowHeight(width: number): number {
  if (width <= 0) {
    return 0;
  }
  return width >= JUMP_SHADOW_FLAT_BELOW ? 4 : 3;
}
